import React, { createContext, useEffect, useState, useCallback } from "react";
import { cloneDeep, find, flatten, sortBy, isNaN } from "lodash";

import { rateAdjusterToLabel, RateAdjusters } from "@eave/shared/types/api";

import { getConfig, isProduction } from "config";
import Spinner from "web_modules/shared/Spinner";
import {
  getLoanProgramsForLoanType,
  getRateForLoanProgram,
  createRateForLoanProgram,
  getAdjustersForLoanType,
  updateAdjustersForLoanType,
  updateLoanType,
  refreshRates,
} from "./api";
import { validateMatrix } from "../RatesEditor/FicoLtvMatrix/DataMatrix";
import { ILoanType, IRate, IRateAdjusters } from "../interfaces";

const Pusher = require("pusher-js");

const RATE_SWING_THRESHOLD = 0.25;

interface Props {
  loanType: ILoanType | null;
  updatedLoanType: ILoanType | null;
  editLoanType: (key: string, value: string) => void;
  busy: boolean;
  currentRates: IRate[];
  updatedRates: IRate[];
  currentAdjusters: IRateAdjusters | null;
  updatedAdjusters: IRateAdjusters | null;
  editMatrix: (value: string, y: number, x: number) => void;
  hasEdits: boolean;
  validationErrors: string[];
  editRate: (loanProgram: string, value: string) => void;
  editAdjuster: (key: string, value: string) => void;
  editLtvAdjuster: (key: string, i: number, value: string) => void;
  undoEdits: () => void;
  saveEdits: () => Promise<[void, void, void]>;
  refreshRatesForLoanType: (loanType: string) => Promise<void>;
  refreshRateStatus: string | null;
}

export const RatesContext = createContext<Props>({
  loanType: null,
  updatedLoanType: null,
  editLoanType: (_: string, __: string) => {},
  busy: false,
  currentRates: [] as IRate[],
  updatedRates: [] as IRate[],
  currentAdjusters: null,
  updatedAdjusters: null,
  editMatrix: (_, __, ___) => {},
  hasEdits: false,
  validationErrors: [] as string[],
  editRate: (_: string, __: string) => {},
  editAdjuster: (_: string, __: string) => {},
  editLtvAdjuster: (_: string, __: number, ___: string) => {},
  undoEdits: () => {},
  saveEdits: () => Promise.resolve([null, null, null]),
  refreshRatesForLoanType: (_: string) => Promise.resolve(undefined),
  refreshRateStatus: null,
});

const RatesProvider = ({
  loanType,
  children,
}: {
  loanType: ILoanType;
  children: React.ReactNode;
}) => {
  const [ready, setReady] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  const [currentLoanType, setCurrentLoanType] = useState<ILoanType>(loanType);
  const [updatedLoanType, setUpdatedLoanType] = useState<ILoanType>(loanType);

  const [currentRates, setCurrentRates] = useState<IRate[]>([]);
  const [updatedRates, setUpdatedRates] = useState<IRate[]>([]);

  const [currentAdjusters, setCurrentAdjusters] = useState<IRateAdjusters>(null);
  const [updatedAdjusters, setUpdatedAdjusters] = useState<IRateAdjusters>(null);

  const [refreshRateStatus, setRefreshRateStatus] = useState<string>(null);

  // Constants inside functions get recreated on every run. So if we're using
  // them as dependencies of hooks, in this case useEffect(), we need to wrap
  // them in a useCallback() which prevents the recreation of objects getting
  // more predictable runs of functions.
  const resetLoanType = useCallback(
    (inputLoanType: ILoanType) => {
      setCurrentLoanType(inputLoanType);
      setUpdatedLoanType(inputLoanType);
    },
    [setCurrentLoanType, setUpdatedLoanType],
  );

  const pusher = new Pusher(getConfig("pusher.key"), {
    encrypted: true,
    cluster: "mt1",
  });

  const setupPusher = () => {
    const prodPrefix = isProduction() ? "eave_" : "";
    const channelName = `${prodPrefix}base_rates`;
    const channel = pusher.subscribe(channelName);

    channel.bind(currentLoanType.name, message => {
      setRefreshRateStatus(message);
    });
  };

  const resetRates = useCallback(
    (rates: IRate[]) => {
      const sorted = sortBy(rates, r => r.loan_program_id); // stable
      setCurrentRates(sorted);
      setUpdatedRates(sorted);
    },
    [setCurrentRates, setUpdatedRates],
  );

  const resetAdjusters = useCallback(
    (adjusters: IRateAdjusters) => {
      setCurrentAdjusters(adjusters);
      setUpdatedAdjusters(adjusters);
    },
    [setCurrentAdjusters, setUpdatedAdjusters],
  );

  const init = useCallback(async () => {
    const programs = await getLoanProgramsForLoanType(currentLoanType.id);

    const calls = [];
    programs.forEach(p => calls.push(getRateForLoanProgram(p.id, p.name)));
    const rates = await Promise.all(calls);
    resetRates(flatten(rates));

    const adjusters = await getAdjustersForLoanType(currentLoanType.id);
    // Assert valid matrix data
    const {
      fico_x_ltv: { matrix, fico, ltv },
    } = adjusters;
    const errMsgs = validateMatrix(matrix, ltv, fico);
    if (errMsgs.length > 0) {
      alert(`There was a problem loading the FICO x LTV matrix data. Please contact eng.`);
      throw new Error(`${errMsgs.join("\n")}`);
    }
    resetAdjusters(adjusters);

    setupPusher();
  }, [currentLoanType.id, resetAdjusters, resetRates]);

  useEffect(() => {
    init()
      .then(() => setReady(true))
      .catch(e => {
        window.alert("There was a problem loading this page! Please contact eng.");
        throw e;
      });
  }, [init, setReady]);

  const hasRatesEdits = useCallback((): boolean => {
    for (let i = 0, l = currentRates.length; i < l; i++) {
      const current = currentRates[i];
      const updated = find(updatedRates, r => r.loan_program_id === current.loan_program_id);
      if (parseFloat(current.rate) !== parseFloat(updated.rate)) {
        return true;
      }
    }
    return false;
  }, [currentRates, updatedRates]);

  // eslint-disable-next-line no-underscore-dangle
  const _hasFicoLtvAdjustersEdits = (): boolean => {
    const {
      fico_x_ltv: { matrix: m1 },
    } = currentAdjusters;
    const {
      fico_x_ltv: { matrix: m2 },
    } = updatedAdjusters;
    for (let y = 0; y < m1.length; y++) {
      for (let x = 0; x < m1[y].length; x++) {
        if (Number(m1[y][x]).toFixed(3) !== Number(m2[y][x]).toFixed(3)) {
          return true;
        }
      }
    }
    return false;
  };

  // eslint-disable-next-line no-underscore-dangle
  const _hasStandardAdjustersEdits = (): boolean => {
    const { adjusters: current } = currentAdjusters;
    const { adjusters: updated } = updatedAdjusters;

    const keys = Object.keys(current);
    for (let i = 0, l = keys.length; i < l; i++) {
      const k = keys[i];
      if (parseFloat(current[k]) !== parseFloat(updated[k])) {
        return true;
      }
    }

    return false;
  };

  // eslint-disable-next-line no-underscore-dangle
  const _hasLTVAdjustersEdits = (): boolean => {
    const { ltv_adjusters: current } = currentAdjusters;
    const { ltv_adjusters: updated } = updatedAdjusters;

    const keys = Object.keys(current);
    for (let i = 0, l = keys.length; i < l; i++) {
      const k = keys[i];
      if (parseFloat(current[k][i]) !== parseFloat(updated[k][i])) {
        return true;
      }
    }

    return false;
  };

  const hasAdjustersEdits = (): boolean =>
    _hasFicoLtvAdjustersEdits() || _hasStandardAdjustersEdits() || _hasLTVAdjustersEdits();

  const hasLoanTypeEdits = (): boolean =>
    currentLoanType.target_price !== updatedLoanType.target_price ||
    currentLoanType.target_purchase_price !== updatedLoanType.target_purchase_price ||
    currentLoanType.target_loan_amount !== updatedLoanType.target_loan_amount ||
    currentLoanType.target_fico !== updatedLoanType.target_fico;

  const hasEdits = (): boolean => hasLoanTypeEdits() || hasRatesEdits() || hasAdjustersEdits();

  const ratesValidationErrors = (): string[] => {
    const errs = [];

    updatedRates.forEach(r => {
      const n = parseFloat(r.rate);
      if (isNaN(n) || n < 0) {
        errs.push(`"${r.loan_program_name}" base rate must be a positive number`);
      }
    });

    return errs;
  };

  const standardAdjustersValidationErrors = (): string[] => {
    const { adjusters: updated } = updatedAdjusters;

    const errs = [];
    [RateAdjusters.ExtraDownpaymentTier1, RateAdjusters.ExtraDownpaymentTier2].forEach(a => {
      if (Number(updated[a]) > 0) {
        const label = rateAdjusterToLabel(a);
        errs.push(`"${label}" adjuster must be negative or zero`);
      }
    });

    return errs;
  };

  const ficoLtvAdjustersValidationErrors = (): string[] => {
    const {
      fico_x_ltv: { matrix, fico, ltv },
    } = updatedAdjusters;

    const errs = [];

    matrix.forEach((row, y) => {
      row.forEach((val, x) => {
        if (isNaN(+val)) {
          errs.push(`FICO "${fico[y]}" x LTV "${ltv[x]}" adjuster is not a valid number`);
        }
      });
    });

    return errs;
  };

  const ltvAdjustersValidationErrors = (): string[] => {
    const { ltv_adjusters } = updatedAdjusters;

    const errs = [];
    Object.keys(ltv_adjusters).forEach(key => {
      ltv_adjusters[key].forEach((val, i) => {
        if (isNaN(+val)) {
          errs.push(
            `LTV adjuster ${rateAdjusterToLabel(key as RateAdjusters)} at position ${
              i + 1
            } is not a valid number`,
          );
        }
      });
    });
    return errs;
  };

  const validationErrors = (): string[] => [
    ...ratesValidationErrors(),
    ...standardAdjustersValidationErrors(),
    ...ficoLtvAdjustersValidationErrors(),
    ...ltvAdjustersValidationErrors(),
  ];

  const loanTypeConfirmationMessages = (): string[] => {
    const messages = [];
    if (currentLoanType.target_price !== updatedLoanType.target_price) {
      messages.push(
        `You are changing ${currentLoanType.name}'s target price from ${currentLoanType.target_price} to ${updatedLoanType.target_price}'`,
      );
    }

    if (currentLoanType.target_purchase_price !== updatedLoanType.target_purchase_price) {
      messages.push(
        `You are changing ${currentLoanType.name}'s target purchase price from ${currentLoanType.target_purchase_price} to ${updatedLoanType.target_purchase_price}'`,
      );
    }

    if (currentLoanType.target_loan_amount !== updatedLoanType.target_loan_amount) {
      messages.push(
        `You are changing ${currentLoanType.name}'s target loan amount from ${currentLoanType.target_loan_amount} to ${updatedLoanType.target_loan_amount}'`,
      );
    }

    if (currentLoanType.target_fico !== updatedLoanType.target_fico) {
      messages.push(
        `You are changing ${currentLoanType.name}'s target FICO from ${currentLoanType.target_fico} to ${updatedLoanType.target_fico}'`,
      );
    }

    return messages;
  };

  const ratesConfirmationMessages = (): string[] =>
    currentRates.reduce((msg, current, i) => {
      const updated = find(updatedRates, r => r.loan_program_id === current.loan_program_id);
      if (Math.abs(parseFloat(current.rate) - parseFloat(updated.rate)) >= RATE_SWING_THRESHOLD) {
        msg.push(
          `You are changing "${currentRates[i].loan_program_name}" from ${current.rate}% to ${updated.rate}%`,
        );
      }
      return msg;
    }, []);

  const standardAdjustersConfirmationMessages = (): string[] => {
    const msg = [];
    const { adjusters: current } = currentAdjusters;
    const { adjusters: updated } = updatedAdjusters;

    const keys = Object.keys(current);
    keys.forEach((k: RateAdjusters) => {
      if (Math.abs(parseFloat(current[k]) - parseFloat(updated[k])) >= RATE_SWING_THRESHOLD) {
        msg.push(
          `You are changing "${rateAdjusterToLabel(k)}" from ${current[k]}% to ${updated[k]}%`,
        );
      }
    });

    return msg;
  };

  const ficoLtvAdjustersConfirmationMessages = (): string[] => {
    const msg = [];
    const {
      fico_x_ltv: { matrix: m1, fico, ltv },
    } = currentAdjusters;
    const {
      fico_x_ltv: { matrix: m2 },
    } = updatedAdjusters;

    for (let y = 0; y < m1.length; y++) {
      for (let x = 0; x < m1[y].length; x++) {
        if (Math.abs(parseFloat(m1[y][x]) - parseFloat(m2[y][x])) >= RATE_SWING_THRESHOLD) {
          msg.push(
            `You are changing "FICO ${fico[y]} and LTV ${ltv[x]}" from ${m1[y][x]} to ${m2[y][x]}`,
          );
        }
      }
    }

    return msg;
  };

  const ltvAdjustersConfirmationMessages = (): string[] => {
    const msg = [];
    const { ltv_adjusters: current } = currentAdjusters;
    const { ltv_adjusters: updated } = updatedAdjusters;

    Object.keys(current).forEach((k: RateAdjusters) => {
      current[k].forEach((val, i) => {
        if (
          Math.abs(parseFloat(current[k][i]) - parseFloat(updated[k][i])) >= RATE_SWING_THRESHOLD
        ) {
          msg.push(
            `You are changing "${rateAdjusterToLabel(k)}" from ${current[k][i]}% to ${
              updated[k][i]
            }%`,
          );
        }
      });
    });

    return msg;
  };

  const confirmationMessages = (): string[] => [
    ...loanTypeConfirmationMessages(),
    ...ratesConfirmationMessages(),
    ...standardAdjustersConfirmationMessages(),
    ...ficoLtvAdjustersConfirmationMessages(),
    ...ltvAdjustersConfirmationMessages(),
  ];

  const editLoanType = (key: string, value: string) => {
    switch (key) {
      case "Target price": {
        setUpdatedLoanType({ ...updatedLoanType, target_price: value });
        break;
      }
      case "Target purchase price": {
        setUpdatedLoanType({ ...updatedLoanType, target_purchase_price: value });
        break;
      }
      case "Target loan amount": {
        setUpdatedLoanType({ ...updatedLoanType, target_loan_amount: value });
        break;
      }
      case "Target FICO": {
        setUpdatedLoanType({ ...updatedLoanType, target_fico: Number(value) });
        break;
      }
      default:
        break;
    }
  };

  const editRate = (name: string, value: string) => {
    const toEdit = find(updatedRates, r => r.loan_program_name === name);
    setUpdatedRates([
      ...updatedRates.filter(r => r.loan_program_name !== name),
      { ...toEdit, rate: value },
    ]);
  };

  const editAdjuster = (key: string, value: string) => {
    setUpdatedAdjusters({
      ...updatedAdjusters,
      adjusters: {
        ...updatedAdjusters.adjusters,
        [key]: value,
      },
    });
  };

  const editLtvAdjuster = (key: string, i: number, value: string) => {
    const a = [...updatedAdjusters.ltv_adjusters[key]];
    a[i] = value;

    setUpdatedAdjusters({
      ...updatedAdjusters,
      ltv_adjusters: {
        ...updatedAdjusters.ltv_adjusters,
        [key]: a,
      },
    });
  };

  const editMatrix = (value: string, y: number, x: number) => {
    const copy = cloneDeep(updatedAdjusters);
    copy.fico_x_ltv.matrix[y][x] = value.trim();
    setUpdatedAdjusters(copy);
  };

  const undoEdits = () => {
    setUpdatedLoanType(currentLoanType);
    setUpdatedRates(currentRates);
    setUpdatedAdjusters(currentAdjusters);
  };

  const saveLoanTypeEdits = async () => {
    if (!hasLoanTypeEdits()) {
      return Promise.resolve();
    }

    const newLoanType = await updateLoanType(
      currentLoanType.id,
      updatedLoanType.target_price,
      updatedLoanType.target_purchase_price,
      updatedLoanType.target_loan_amount,
      updatedLoanType.target_fico,
    );
    resetLoanType(newLoanType);

    return null;
  };

  const saveRatesEdits = async () => {
    if (!hasRatesEdits()) {
      return Promise.resolve();
    }

    const unchanged = [];
    const calls = [];

    currentRates.forEach(current => {
      const updated = find(updatedRates, r => r.loan_program_name === current.loan_program_name);
      if (parseFloat(current.rate) === parseFloat(updated.rate)) {
        unchanged.push(current);
      } else {
        calls.push(
          createRateForLoanProgram(
            current.loan_program_id,
            current.loan_program_name,
            updated.rate,
          ),
        );
      }
    });

    return Promise.all(calls)
      .then(rates => resetRates([...unchanged, ...rates]))
      .catch(e => {
        setUpdatedRates(currentRates);
        window.alert("There was a problem saving base rates! Please contact eng.");
        throw e;
      });
  };

  const saveAdjustersEdits = async () => {
    if (!hasAdjustersEdits()) {
      return Promise.resolve();
    }

    return updateAdjustersForLoanType(currentLoanType.id, updatedAdjusters)
      .then(adjusters => {
        resetAdjusters(adjusters);
      })
      .catch(e => {
        setUpdatedAdjusters(currentAdjusters);
        window.alert("There was a problem saving rate adjusters! Please contact eng.");
        throw e;
      });
  };

  const refreshRatesForLoanType = (): Promise<void> => {
    setBusy(true);
    return refreshRates(currentLoanType.name)
      .then(message => {
        setRefreshRateStatus(message);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const saveEdits = (): Promise<[void, void, void]> => {
    const msg = confirmationMessages();

    // skips confirmation
    if (msg.length === 0) {
      setBusy(true);
      return Promise.all([saveLoanTypeEdits(), saveRatesEdits(), saveAdjustersEdits()]).finally(
        () => {
          setBusy(false);
        },
      );
    }

    // saves only if confirmed
    const confirmed = window.confirm(
      `Please confirm the rate changes:\n\t * ${msg.join("\n\t * ")}`,
    );
    if (confirmed) {
      setBusy(true);
      return Promise.all([saveLoanTypeEdits(), saveRatesEdits(), saveAdjustersEdits()]).finally(
        () => {
          setBusy(false);
        },
      );
    }

    return Promise.resolve([null, null, null]);
  };

  if (!ready) {
    return <Spinner active />;
  }

  return (
    <RatesContext.Provider
      value={{
        loanType: currentLoanType,
        updatedLoanType,
        editLoanType,
        busy,
        currentRates,
        updatedRates,
        currentAdjusters,
        updatedAdjusters,
        hasEdits: hasEdits(),
        validationErrors: validationErrors(),
        editRate,
        editAdjuster,
        editLtvAdjuster,
        editMatrix,
        undoEdits,
        saveEdits,
        refreshRatesForLoanType,
        refreshRateStatus,
      }}
    >
      {children}
    </RatesContext.Provider>
  );
};

export default RatesProvider;
